import { useState, useRef, useEffect } from 'react';
import type { Notification, NotificationType, NotificationTarget } from '../types';

interface NotificationModalProps {
  notification?: Notification;
  onClose: () => void;
  onSave: (notification: Partial<Notification>) => void;
}

export default function NotificationModal({ notification, onClose, onSave }: NotificationModalProps) {
  const [formData, setFormData] = useState({
    title: notification?.title || '',
    type: (notification?.type || 'platform') as NotificationType,
    content: notification?.content || '',
    notificationTime: notification?.notificationTime || '',
    target: (notification?.target || 'all-users') as NotificationTarget
  });

  const [errors, setErrors] = useState({
    title: '',
    content: '',
    notificationTime: ''
  });

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.innerHTML = formData.content;
    }
  }, []);

  const handleContentChange = () => {
    if (contentRef.current) {
      setFormData({ ...formData, content: contentRef.current.innerHTML });
    }
  };

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    handleContentChange();
  };

  const validate = () => {
    const newErrors = {
      title: '',
      content: '',
      notificationTime: ''
    };

    if (!formData.title.trim()) {
      newErrors.title = '请输入通知标题';
    } else if (formData.title.length > 50) {
      newErrors.title = '通知标题不能超过50个字符';
    }

    if (!formData.content.trim() || formData.content === '<br>') {
      newErrors.content = '请输入通知内容';
    }

    if (!formData.notificationTime) {
      newErrors.notificationTime = '请选择通知时间';
    }

    setErrors(newErrors);
    return !newErrors.title && !newErrors.content && !newErrors.notificationTime;
  };

  const handleSave = () => {
    if (validate()) {
      onSave({
        ...formData,
        id: notification?.id
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {notification ? '编辑通知' : '创建通知'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              通知标题 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="请输入通知标题（不超过50个字符）"
              maxLength={50}
              className={`w-full px-4 py-2 border rounded-lg text-sm ${
                errors.title ? 'border-red-300' : 'border-gray-200'
              }`}
            />
            {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
            <p className="text-xs text-gray-500 mt-1">{formData.title.length}/50</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              通知类型 <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={formData.type === 'platform'}
                  onChange={() => setFormData({ ...formData, type: 'platform' })}
                  className="w-4 h-4 text-teal-500 border-gray-300 cursor-pointer"
                />
                <span className="text-sm text-gray-700">平台通知</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={formData.type === 'activity'}
                  onChange={() => setFormData({ ...formData, type: 'activity' })}
                  className="w-4 h-4 text-teal-500 border-gray-300 cursor-pointer"
                />
                <span className="text-sm text-gray-700">活动通知</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              通知内容 <span className="text-red-500">*</span>
            </label>
            <div className={`border rounded-lg ${errors.content ? 'border-red-300' : 'border-gray-200'}`}>
              <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50">
                <button
                  onClick={() => execCommand('bold')}
                  className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded cursor-pointer"
                  title="加粗"
                >
                  <i className="ri-bold text-sm"></i>
                </button>
                <button
                  onClick={() => execCommand('italic')}
                  className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded cursor-pointer"
                  title="斜体"
                >
                  <i className="ri-italic text-sm"></i>
                </button>
                <button
                  onClick={() => execCommand('underline')}
                  className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded cursor-pointer"
                  title="下划线"
                >
                  <i className="ri-underline text-sm"></i>
                </button>
                <div className="w-px h-6 bg-gray-300 mx-1"></div>
                <button
                  onClick={() => execCommand('insertUnorderedList')}
                  className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded cursor-pointer"
                  title="无序列表"
                >
                  <i className="ri-list-unordered text-sm"></i>
                </button>
                <button
                  onClick={() => execCommand('insertOrderedList')}
                  className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded cursor-pointer"
                  title="有序列表"
                >
                  <i className="ri-list-ordered text-sm"></i>
                </button>
              </div>
              <div
                ref={contentRef}
                contentEditable
                onInput={handleContentChange}
                className="min-h-[200px] p-4 text-sm text-gray-900 outline-none"
                placeholder="请输入通知内容"
              />
            </div>
            {errors.content && <p className="text-xs text-red-500 mt-1">{errors.content}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              通知时间 <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={formData.notificationTime.replace(' ', 'T').substring(0, 16)}
              onChange={(e) => setFormData({ ...formData, notificationTime: e.target.value.replace('T', ' ') + ':00' })}
              className={`w-full px-4 py-2 border rounded-lg text-sm cursor-pointer ${
                errors.notificationTime ? 'border-red-300' : 'border-gray-200'
              }`}
            />
            {errors.notificationTime && <p className="text-xs text-red-500 mt-1">{errors.notificationTime}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              通知对象 <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={formData.target === 'all-users'}
                  onChange={() => setFormData({ ...formData, target: 'all-users' })}
                  className="w-4 h-4 text-teal-500 border-gray-300 cursor-pointer"
                />
                <span className="text-sm text-gray-700">全体用户</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={formData.target === 'all-schools'}
                  onChange={() => setFormData({ ...formData, target: 'all-schools' })}
                  className="w-4 h-4 text-teal-500 border-gray-300 cursor-pointer"
                />
                <span className="text-sm text-gray-700">全体学校（管理员）</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={formData.target === 'all-enterprises'}
                  onChange={() => setFormData({ ...formData, target: 'all-enterprises' })}
                  className="w-4 h-4 text-teal-500 border-gray-300 cursor-pointer"
                />
                <span className="text-sm text-gray-700">全体企业（管理员）</span>
              </label>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 bg-teal-500 text-white text-sm rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
}
